import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LocalizationDemoComponent } from './shared/components/localization-demo/localization-demo.component';
import { SuperAdminGuard } from './core/guards/super-admin.guard';
import { PermissionGuard } from './core/guards/permission.guard';
import { SitesListComponent } from './features/admin/sites/sites-list/sites-list.component';
import { TenantsListComponent } from './features/admin/tenants/tenants-list/tenants-list.component';
import { TenantFormComponent } from './features/admin/tenants/tenant-form/tenant-form.component';
import { DiningAreasListComponent } from './features/admin/restaurant-config/dining-areas/dining-areas-list/dining-areas-list.component';
import { TablesListComponent } from './features/admin/restaurant-config/restaurant-tables/tables-list/tables-list.component';
import { MenuCategoriesListComponent } from './features/admin/restaurant-config/menu-categories/menu-categories-list/menu-categories-list.component';
import { MenuItemsListComponent } from './features/admin/restaurant-config/menu-items/menu-items-list/menu-items-list.component';
import { ModifiersListComponent } from './features/admin/restaurant-config/modifiers/modifiers-list/modifiers-list.component';
import { TaxesListComponent } from './features/admin/restaurant-config/taxes/taxes-list/taxes-list.component';
import { PaymentMethodsListComponent } from './features/admin/restaurant-config/payment-methods/payment-methods-list/payment-methods-list.component';
import { NotFoundComponent } from './features/not-found/not-found.component';
const routes: Routes = [
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full',
  },
  {
    path: 'localization-demo',
    component: LocalizationDemoComponent,
  },
  {
    path: 'auth',
    loadChildren: () =>
      import('./features/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'legal',
    loadChildren: () =>
      import('./features/legal/legal.module').then((m) => m.LegalModule),
  },
  {
    path: 'dashboard',
    //canActivate: [AuthGuard],
    loadChildren: () =>
      import('./features/dashboard/dashboard.module').then(
        (m) => m.DashboardModule,
      ),
  },
  {
    path: 'pos',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['pos.access', 'order.read'], title: 'Point of Sale' },
    loadChildren: () =>
      import('./features/pos/pos.module').then((m) => m.PosModule),
  },
  {
    path: 'kds',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['kitchen.view'], title: 'Kitchen Display' },
    loadChildren: () =>
      import('./features/kds/kds.module').then((m) => m.KdsModule),
  },
  {
    path: 'menu-display',
    // Customer-facing digital-signage menu board (full-screen, no sidebar).
    // No permission guard — any logged-in session may render it; the data
    // request still carries the auth token via the HTTP interceptor.
    data: { title: 'Menu Display' },
    loadChildren: () =>
      import('./features/menu-display/menu-display.module').then(
        (m) => m.MenuDisplayModule,
      ),
  },
  {
    path: 'orders',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['order.read', 'pos.access'], title: 'Order Management' },
    loadChildren: () =>
      import('./features/order-management/order-management.module').then(
        (m) => m.OrderManagementModule,
      ),
  },
  {
    path: 'transactions',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['order.read', 'pos.access'], title: 'Transactions' },
    loadChildren: () =>
      import('./features/transactions/transactions.module').then(
        (m) => m.TransactionsModule,
      ),
  },
  {
    path: 'customers',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['customer.read'], title: 'Customers' },
    loadChildren: () =>
      import('./features/customers/customers.module').then(
        (m) => m.CustomersModule,
      ),
  },
  {
    path: 'reports',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['report.view'], title: 'Reports' },
    loadChildren: () =>
      import('./features/reports/reports.module').then((m) => m.ReportsModule),
  },
  {
    path: 'reservations',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['reservation.read'], title: 'Reservations' },
    loadChildren: () =>
      import('./features/reservations/reservations.module').then(
        (m) => m.ReservationsModule,
      ),
  },
  {
    path: 'users',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['user.management'] },
    loadChildren: () =>
      import('./features/users/users.module').then((m) => m.UsersModule),
  },
  {
    path: 'settings',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['settings.read', 'settings.update'] },
    loadChildren: () =>
      import('./features/settings/settings.module').then(
        (m) => m.SettingsModule,
      ),
  },
  {
    path: 'roles',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['role.read'] },
    loadChildren: () =>
      import('./features/roles/roles.module').then((m) => m.RolesModule),
  },
  {
    path: 'admin/audit-logs',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['audit.read'], title: 'Audit Logs' },
    loadChildren: () =>
      import('./features/admin/audit-logs/audit-logs.module').then(
        (m) => m.AuditLogsModule,
      ),
  },
  {
    path: 'admin/sites',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['user.management', 'settings.read'] },
    children: [
      {
        path: '',
        component: SitesListComponent,
        data: { title: 'Sites Management' },
      },
    ],
  },
  {
    path: 'admin/tenants',
    canActivate: [SuperAdminGuard],
    children: [
      {
        path: '',
        component: TenantsListComponent,
        data: { title: 'Tenants Management' },
      },
      {
        path: 'new',
        component: TenantFormComponent,
        data: { title: 'Create Tenant' },
      },
      {
        path: ':id/edit',
        component: TenantFormComponent,
        data: { title: 'Edit Tenant' },
      },
    ],
  },
  {
    path: 'config/dining-areas',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['dining_area.read'], title: 'Dining Areas' },
    component: DiningAreasListComponent,
  },
  {
    path: 'config/tables',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['table.read'], title: 'Tables' },
    component: TablesListComponent,
  },
  {
    path: 'config/menu-categories',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['menu_category.read'], title: 'Menu Categories' },
    component: MenuCategoriesListComponent,
  },
  {
    path: 'config/menu-items',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['menu_item.read'], title: 'Menu Items' },
    component: MenuItemsListComponent,
  },
  {
    path: 'config/modifiers',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['modifier.read'], title: 'Modifiers' },
    component: ModifiersListComponent,
  },
  {
    path: 'config/taxes',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['tax.read'], title: 'Taxes' },
    component: TaxesListComponent,
  },
  {
    path: 'config/payment-methods',
    canActivate: [PermissionGuard],
    data: { permissionsAny: ['payment_method.read'], title: 'Payment Methods' },
    component: PaymentMethodsListComponent,
  },
  {
    path: 'profile',
    //canActivate: [AuthGuard],
    loadChildren: () =>
      import('./features/profile/profile.module').then((m) => m.ProfileModule),
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
